/**
 * Auth Provider Component
 * Provides authentication context and initialization for React components
 */

import React, { useEffect, useState, type PropsWithChildren } from 'react'
import { useAuthStore } from './auth-store'

interface AuthProviderProps extends PropsWithChildren {
  /**
   * Optional loading component to show while auth is initializing
   */
  LoadingComponent?: React.ComponentType
  /**
   * Whether to show loading state during initialization
   */
  showLoading?: boolean
}

/**
 * Default loading component
 */
const DefaultLoadingComponent: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
    }}
  >
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: '40px',
          height: '40px',
          border: '4px solid var(--app-border)',
          borderTop: '4px solid var(--app-primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px',
        }}
      />
      <p>Authentifizierung wird geladen...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
)

/**
 * Auth Provider - Wrap your app with this to enable authentication
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  LoadingComponent = DefaultLoadingComponent,
  showLoading = true,
}) => {
  const [initialized, setInitialized] = useState(false)
  const { initialize, isLoading } = useAuthStore()

  useEffect(() => {
    const init = async () => {
      await initialize()
      setInitialized(true)
    }
    init()
  }, [initialize])

  if (showLoading && (!initialized || isLoading)) {
    return <LoadingComponent />
  }

  return <>{children}</>
}

/**
 * HOC for wrapping components that need auth
 */
export function withAuth<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options?: {
    LoadingComponent?: React.ComponentType
    showLoading?: boolean
  }
): React.FC<P> {
  const WithAuthComponent: React.FC<P> = props => (
    <AuthProvider {...options}>
      <WrappedComponent {...props} />
    </AuthProvider>
  )

  WithAuthComponent.displayName = `withAuth(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`

  return WithAuthComponent
}
